import React, { useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Rect, Line, Text, Circle } from 'react-konva';
import { useCanvas } from '../../hooks/useCanvas';
import { useAuth } from '../../hooks/useAuth';
import { useCursors } from '../../hooks/useCursors';
import { updateCursorPosition } from '../../services/cursors';
import { generateUserColor, generateDisplayName, throttle, screenToCanvas } from '../../utils/helpers';
import Shape from './Shape';
import LineHandles from './LineHandles';
import EditableText from './EditableText';
import ShortcutsPanel from '../Help/ShortcutsPanel';
import { 
  CANVAS_WIDTH, 
  CANVAS_HEIGHT, 
  VIEWPORT_WIDTH, 
  VIEWPORT_HEIGHT,
  CANVAS_CONFIG
} from '../../utils/constants';

// Grid background component
function GridBackground({ width, height, scale }) {
  const gridSize = 50;
  const lines = [];

  // Only render grid if zoomed in enough
  if (scale < 0.5) return null;

  // Vertical lines
  for (let i = 0; i <= width / gridSize; i++) {
    lines.push(
      <Line
        key={`v${i}`}
        points={[i * gridSize, 0, i * gridSize, height]}
        stroke="#f0f0f0"
        strokeWidth={1 / scale}
        opacity={Math.min(scale, 1)}
      />
    );
  }

  // Horizontal lines
  for (let i = 0; i <= height / gridSize; i++) {
    lines.push(
      <Line
        key={`h${i}`}
        points={[0, i * gridSize, width, i * gridSize]}
        stroke="#f0f0f0"
        strokeWidth={1 / scale}
        opacity={Math.min(scale, 1)}
      />
    );
  }

  return <>{lines}</>;
}

// Canvas component
function Canvas({ showShortcuts, onCloseShortcuts }) {
  const { 
    stageRef, shapes, selectedId, selectedIds, loading, clearSelection, deleteShape, deleteAllShapes, addShape,
    groupShapes, ungroupShapes, getGroupId, getShapesInGroup, selectShapeWithGroup, updateShape,
    previewShapes
  } = useCanvas();
  const { currentUser } = useAuth();
  const cursors = useCursors();
  
  // Track recently added AI shapes for sparkle effect (3 seconds)
  const [recentAIShapes, setRecentAIShapes] = useState(new Set());
  
  // Text editing state
  const [editingTextId, setEditingTextId] = useState(null);
  
  useEffect(() => {
    // Find newly added AI shapes
    const aiShapes = shapes.filter(s => s.createdByAI && s.aiGeneratedAt);
    const now = Date.now();
    const recentThreshold = 3000; // 3 seconds
    
    const newRecentAIShapes = new Set();
    aiShapes.forEach(shape => {
      if (now - shape.aiGeneratedAt < recentThreshold) {
        newRecentAIShapes.add(shape.id);
        // Set timeout to remove sparkle after 3 seconds
        setTimeout(() => {
          setRecentAIShapes(prev => {
            const updated = new Set(prev);
            updated.delete(shape.id);
            return updated;
          });
        }, recentThreshold - (now - shape.aiGeneratedAt));
      }
    });
    
    setRecentAIShapes(newRecentAIShapes);
  }, [shapes]);

  // Auto-detect text shapes that need editing
  useEffect(() => {
    const editingShape = shapes.find(s => s.type === 'text' && s.isEditing);
    if (editingShape) {
      setEditingTextId(editingShape.id);
    } else if (editingTextId && !shapes.find(s => s.id === editingTextId && s.isEditing)) {
      setEditingTextId(null);
    }
  }, [shapes, editingTextId]);

  // Handlers for text editing
  const handleStartTextEdit = useCallback((shapeId) => {
    setEditingTextId(shapeId);
    updateShape(shapeId, { isEditing: true });
  }, [updateShape]);

  const handleFinishTextEdit = useCallback(() => {
    setEditingTextId(null);
  }, []);
  
  const [viewportSize, setViewportSize] = useState({
    width: VIEWPORT_WIDTH(),
    height: VIEWPORT_HEIGHT() - 64 // Account for navbar height
  });
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [isPanning, setIsPanning] = useState(false);

  // Throttled cursor position update for canvas (100ms throttle for performance)
  const throttledCursorUpdate = useCallback(
    throttle(async (canvasX, canvasY) => {
      if (!currentUser?.uid) return;
      
      try {
        const userColor = generateUserColor(currentUser.uid);
        const displayName = generateDisplayName(currentUser);
        
        await updateCursorPosition(
          currentUser.uid,
          canvasX,
          canvasY,
          displayName,
          userColor
        );
      } catch (error) {
        // Silent fail - cursor updates are non-critical
      }
    }, 100), // Increased from 30ms to 100ms to reduce Firebase writes
    [currentUser]
  );

  // Handle mouse move over the canvas
  const handleMouseMove = useCallback((e) => {
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    
    if (!pointer) return;
    
    // Convert screen coordinates to canvas coordinates
    const canvasCoords = screenToCanvas(
      pointer.x, 
      pointer.y, 
      { 
        x: stagePos.x, 
        y: stagePos.y, 
        scaleX: stageScale, 
        scaleY: stageScale 
      }
    );
    
    // Send canvas coordinates to Firebase
    throttledCursorUpdate(canvasCoords.x, canvasCoords.y);
  }, [stagePos.x, stagePos.y, stageScale, throttledCursorUpdate]);

  // Update viewport size on window resize
  useEffect(() => {
    const handleResize = () => {
      setViewportSize({
        width: VIEWPORT_WIDTH(),
        height: VIEWPORT_HEIGHT() - 64 // Account for navbar
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Comprehensive keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = async (e) => {
      // Don't handle shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Detect if on Mac or Windows/Linux
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // ==================== TEXT EDITING MODE ====================
      // Check if a single text shape is selected for direct keyboard editing
      const selectedTextShapes = shapes.filter(shape => 
        selectedIds.includes(shape.id) && shape.type === 'text'
      );
      
      if (selectedTextShapes.length === 1) {
        const textShape = selectedTextShapes[0];
        
        // Handle Backspace - delete last character
        if (e.key === 'Backspace' && !modifier) {
          e.preventDefault();
          const currentText = textShape.text || 'Text';
          const newText = currentText.length > 1 ? currentText.slice(0, -1) : 'Text';
          await updateShape(textShape.id, { text: newText });
          return;
        }
        
        // Handle Delete key - clear all text (reset to default)
        if (e.key === 'Delete' && !e.shiftKey) {
          e.preventDefault();
          await updateShape(textShape.id, { text: 'Text' });
          return;
        }
        
        // Handle Enter - add line break
        if (e.key === 'Enter' && !modifier) {
          e.preventDefault();
          const currentText = textShape.text === 'Text' ? '' : textShape.text;
          const newText = currentText + '\n';
          await updateShape(textShape.id, { text: newText });
          return;
        }
        
        // Handle regular character input (single printable characters)
        if (e.key.length === 1 && !modifier && !e.altKey) {
          e.preventDefault();
          
          // If current text is default "Text", replace it entirely
          const currentText = textShape.text === 'Text' ? '' : textShape.text;
          const newText = currentText + e.key;
          
          await updateShape(textShape.id, { text: newText });
          return;
        }
        
        // If we reach here with a text shape selected, some keys should still work
        // (like Escape, Arrow keys, shortcuts with modifiers)
        // So we continue to check other shortcuts below
      }
      // ==================== END TEXT EDITING MODE ====================
      
      // Help panel (?)
      if (e.key === '?' && !modifier) {
        e.preventDefault();
        if (onCloseShortcuts) {
          // Use parent's handler to show shortcuts
          const syntheticEvent = { preventDefault: () => {}, key: '?' };
          // We'll trigger this through App.jsx state
          window.dispatchEvent(new CustomEvent('showShortcuts'));
        }
        return;
      }
      
      // Close shortcuts panel (Escape when panel is open)
      if (e.key === 'Escape' && showShortcuts) {
        e.preventDefault();
        if (onCloseShortcuts) {
          onCloseShortcuts();
        }
        return;
      }
      
      // Group (Ctrl+G / Cmd+G)
      if (modifier && !e.shiftKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        if (selectedIds.length >= 2) {
          console.log('📦 Grouping', selectedIds.length, 'shapes');
          await groupShapes(selectedIds);
        }
        return;
      }
      
      // Ungroup (Ctrl+Shift+G / Cmd+Shift+G)
      if (modifier && e.shiftKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        if (selectedIds.length > 0) {
          const groupIds = new Set();
          selectedIds.forEach(shapeId => {
            const groupId = getGroupId(shapeId);
            if (groupId) groupIds.add(groupId);
          });
          if (groupIds.size > 0) {
            for (const groupId of groupIds) {
              await ungroupShapes(groupId);
            }
          }
        }
        return;
      }
      
      // Toggle grid (Ctrl+')
      if (modifier && e.key === "'") {
        e.preventDefault();
        setShowGrid(prev => !prev);
        console.log('Grid:', !showGrid ? 'ON' : 'OFF');
        return;
      }
      
      // Reset zoom (Ctrl+0)
      if (modifier && e.key === '0') {
        e.preventDefault();
        setStageScale(1);
        setStagePos({ x: 0, y: 0 });
        console.log('View reset');
        return;
      }
      
      // Zoom in (+, =)
      if ((e.key === '+' || e.key === '=') && !modifier) {
        e.preventDefault();
        setStageScale(prev => Math.min(prev * 1.2, 3));
        return;
      }
      
      // Zoom out (-)
      if (e.key === '-' && !modifier) {
        e.preventDefault();
        setStageScale(prev => Math.max(prev / 1.2, 0.1));
        return;
      }
      
      // Arrow key navigation
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        if (selectedIds.length > 0) {
          e.preventDefault();
          const moveAmount = e.shiftKey ? 10 : 1;
          const deltaX = e.key === 'ArrowLeft' ? -moveAmount : e.key === 'ArrowRight' ? moveAmount : 0;
          const deltaY = e.key === 'ArrowUp' ? -moveAmount : e.key === 'ArrowDown' ? moveAmount : 0;
          
          // Move all selected shapes
          for (const shapeId of selectedIds) {
            const shape = shapes.find(s => s.id === shapeId);
            if (shape && (!shape.isLocked || shape.lockedBy === currentUser?.uid)) {
              await updateShape(shapeId, {
                x: shape.x + deltaX,
                y: shape.y + deltaY
              });
            }
          }
          return;
        }
      }
      
      // Delete/Backspace (only for non-text shapes or multiple selections)
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // If a single text shape is selected, text editing handled above
        const isSingleTextSelected = selectedIds.length === 1 && 
          shapes.find(s => selectedIds.includes(s.id))?.type === 'text';
        
        if (isSingleTextSelected) {
          // Already handled in text editing section above
          return;
        }
        
        // Shift + Delete = Delete All
        if (e.shiftKey) {
          e.preventDefault();
          const confirmed = window.confirm(
            `Are you sure you want to delete ALL ${shapes.length} shapes? This cannot be undone.`
          );
          if (confirmed) {
            deleteAllShapes();
          }
          return;
        }
        
        // Regular Delete = Delete Selected shapes
        if (selectedIds.length > 0) {
          e.preventDefault();
          // Delete all selected shapes
          for (const shapeId of selectedIds) {
            const selectedShape = shapes.find(shape => shape.id === shapeId);
            if (selectedShape?.isLocked && selectedShape?.lockedBy !== currentUser?.uid) {
              console.warn(`Cannot delete shape ${shapeId} - locked by another user`);
              continue;
            }
            deleteShape(shapeId);
          }
        }
        return;
      }
      
      // Escape - deselect
      if (e.key === 'Escape') {
        e.preventDefault();
        clearSelection();
        return;
      }
      
      // Rectangle tool (R)
      if ((e.key === 'r' || e.key === 'R') && !modifier) {
        e.preventDefault();
        addShape({
          x: Math.random() * (CANVAS_WIDTH - 200) + 100,
          y: Math.random() * (CANVAS_HEIGHT - 200) + 100
        });
        return;
      }
      
      // Select tool (V)
      if ((e.key === 'v' || e.key === 'V') && !modifier) {
        e.preventDefault();
        console.log('Select tool activated');
        return;
      }
    };

    const handleKeyUp = (e) => {
      // Handle space key release for panning
      if (e.key === ' ') {
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedId, selectedIds, shapes, currentUser, deleteShape, deleteAllShapes, clearSelection, addShape, groupShapes, ungroupShapes, getGroupId, showShortcuts, showGrid, updateShape]);

  // Drag bound function to constrain stage movement
  const dragBoundFunc = (pos) => {
    const stage = stageRef.current;
    if (!stage) return pos;

    const scale = stage.scaleX();
    const newPos = { ...pos };

    // Calculate boundaries
    const maxX = 0;
    const minX = viewportSize.width - (CANVAS_WIDTH * scale);
    const maxY = 0;
    const minY = viewportSize.height - (CANVAS_HEIGHT * scale);

    // Constrain position to keep canvas visible
    newPos.x = Math.max(minX, Math.min(maxX, newPos.x));
    newPos.y = Math.max(minY, Math.min(maxY, newPos.y));

    return newPos;
  };

  // Handle zoom with wheel
  const handleWheel = (e) => {
    e.evt.preventDefault();

    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    // Calculate zoom direction and factor
    const scaleBy = 1.1;
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    // Constrain zoom level
    const constrainedScale = Math.max(
      CANVAS_CONFIG.minZoom, 
      Math.min(CANVAS_CONFIG.maxZoom, newScale)
    );

    // Calculate new position to zoom towards cursor
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newPos = {
      x: pointer.x - mousePointTo.x * constrainedScale,
      y: pointer.y - mousePointTo.y * constrainedScale,
    };

    // Apply constraints to new position
    const constrainedPos = dragBoundFunc(newPos);

    setStageScale(constrainedScale);
    setStagePos(constrainedPos);
  };

  // Handle stage drag
  const handleStageDragEnd = (e) => {
    setStagePos(e.currentTarget.position());
  };

  // Handle click on empty canvas (deselect)
  const handleStageClick = (e) => {
    // Check if we clicked on empty canvas (stage, layer, or background)
    const target = e.target;
    const isStage = target === target.getStage();
    const isBackground = target.name() === 'background';
    const isLayer = target.getClassName() === 'Layer';
    
    const clickedOnEmpty = isStage || isBackground || isLayer;
    
    if (clickedOnEmpty) {
      clearSelection();
    }
  };

  // Show loading state while shapes are loading
  if (loading) {
    return (
      <div className="w-full h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Loading canvas...
          </h2>
          <p className="text-gray-600">
            Syncing with collaborators
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-50 relative overflow-hidden">
        <Stage
          ref={stageRef}
          width={viewportSize.width}
          height={viewportSize.height}
          x={stagePos.x}
          y={stagePos.y}
          scaleX={stageScale}
          scaleY={stageScale}
          draggable={true}
          dragBoundFunc={dragBoundFunc}
          onWheel={handleWheel}
          onDragEnd={handleStageDragEnd}
          onClick={handleStageClick}
          onMouseMove={handleMouseMove}
          style={{ backgroundColor: '#fafafa' }}
        >
          {/* Background Layer */}
          <Layer>
            {/* Canvas boundary rectangle */}
            <Rect
              name="background"
              x={0}
              y={0}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              fill="white"
              stroke="#e5e5e5"
              strokeWidth={2}
              shadowColor="rgba(0,0,0,0.1)"
              shadowBlur={10}
              shadowOffset={{ x: 0, y: 2 }}
            />
            
            {/* Grid background */}
            {showGrid && (
              <GridBackground 
                width={CANVAS_WIDTH} 
                height={CANVAS_HEIGHT} 
                scale={stageScale} 
              />
            )}
          </Layer>

          {/* Shapes Layer */}
          <Layer>
            {shapes.map((shape) => (
              <React.Fragment key={shape.id}>
                <Shape
                  {...shape}
                  isSelected={selectedIds.includes(shape.id)}
                  isRecentAI={recentAIShapes.has(shape.id)}
                />
                {/* Line endpoint handles for selected lines */}
                <LineHandles
                  line={shape}
                  isSelected={selectedIds.includes(shape.id)}
                  onUpdate={updateShape}
                />
              </React.Fragment>
            ))}
            
            {/* AI Preview Shapes */}
            {previewShapes && previewShapes.length > 0 && previewShapes.map((previewShape, index) => {
              const { type = 'rectangle', x = 0, y = 0, width = 100, height = 100, radius = 50, text = 'Text', fontSize = 18, points = [0, 0, 100, 0], stroke = '#000000', fill = '#cccccc' } = previewShape;
              
              const commonProps = {
                listening: false, // Non-interactive
                opacity: 0.5, // Semi-transparent
                stroke: '#10b981', // Green border
                strokeWidth: 2,
                dash: [10, 5], // Dashed border
                shadowColor: 'rgba(16, 185, 129, 0.4)',
                shadowBlur: 10,
              };
              
              // Render based on type
              if (type === 'circle') {
                return (
                  <Circle
                    key={`preview-${index}`}
                    x={x}
                    y={y}
                    radius={radius}
                    fill={fill}
                    {...commonProps}
                  />
                );
              } else if (type === 'text') {
                return (
                  <Text
                    key={`preview-${index}`}
                    x={x}
                    y={y}
                    text={text}
                    fontSize={fontSize}
                    fill={fill}
                    {...commonProps}
                  />
                );
              } else if (type === 'line') {
                return (
                  <Line
                    key={`preview-${index}`}
                    x={x}
                    y={y}
                    points={points}
                    stroke={stroke}
                    {...commonProps}
                  />
                );
              } else {
                // Rectangle (default)
                return (
                  <Rect
                    key={`preview-${index}`}
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={fill}
                    {...commonProps}
                  />
                );
              }
            })}
            
            {/* Group Bounding Boxes */}
            {(() => {
              // Group shapes by groupId
              const groupsMap = new Map();
              shapes.forEach(shape => {
                if (shape.groupId) {
                  if (!groupsMap.has(shape.groupId)) {
                    groupsMap.set(shape.groupId, []);
                  }
                  groupsMap.get(shape.groupId).push(shape);
                }
              });

              // Render bounding box for each group
              return Array.from(groupsMap.entries()).map(([groupId, groupShapes]) => {
                // Calculate bounding box for all shapes in group
                let minX = Infinity, minY = Infinity;
                let maxX = -Infinity, maxY = -Infinity;

                groupShapes.forEach(shape => {
                  const { x = 0, y = 0, width = 100, height = 100 } = shape;
                  minX = Math.min(minX, x);
                  minY = Math.min(minY, y);
                  maxX = Math.max(maxX, x + width);
                  maxY = Math.max(maxY, y + height);
                });

                const padding = 8;
                return (
                  <Rect
                    key={`group-${groupId}`}
                    x={minX - padding}
                    y={minY - padding}
                    width={maxX - minX + padding * 2}
                    height={maxY - minY + padding * 2}
                    stroke="#9333ea"
                    strokeWidth={2}
                    dash={[10, 5]}
                    listening={false}
                    opacity={0.6}
                    shadowColor="rgba(147, 51, 234, 0.3)"
                    shadowBlur={8}
                  />
                );
              });
            })()}
          </Layer>

          {/* Cursors Layer - Show other users' cursors */}
          <Layer>
            {Object.values(cursors).map((cursor) => (
              <React.Fragment key={cursor.userId}>
                {/* Cursor pointer */}
                <Rect
                  x={cursor.cursorX - 2}
                  y={cursor.cursorY - 2}
                  width={4}
                  height={4}
                  fill={cursor.cursorColor}
                  cornerRadius={2}
                />
                {/* User name label */}
                <Rect
                  x={cursor.cursorX + 10}
                  y={cursor.cursorY - 20}
                  width={cursor.displayName.length * 8 + 10}
                  height={20}
                  fill={cursor.cursorColor}
                  cornerRadius={4}
                  opacity={0.8}
                />
                <Text
                  x={cursor.cursorX + 15}
                  y={cursor.cursorY - 15}
                  text={cursor.displayName}
                  fontSize={12}
                  fontFamily="Arial"
                  fill="white"
                />
              </React.Fragment>
            ))}
          </Layer>
        </Stage>
        
        {/* Keyboard Shortcuts Panel */}
        <ShortcutsPanel 
          isOpen={showShortcuts} 
          onClose={onCloseShortcuts} 
        />

        {/* Editable Text Input (rendered outside Konva Stage) */}
        {editingTextId && (() => {
          const editingShape = shapes.find(s => s.id === editingTextId);
          return editingShape ? (
            <EditableText
              shape={editingShape}
              onUpdate={updateShape}
              onFinishEdit={handleFinishTextEdit}
              stageScale={stageScale}
              stagePosition={stagePos}
            />
          ) : null;
        })()}
    </div>
  );
}

export default Canvas;